'use client';

import { bridge } from '@wwe-portal/web/libs/bridge';
import {
  clearLoginProvider,
  getLoginProvider,
  setLoginProvider,
} from '@wwe-portal/web/libs/bridge/auth';
import {
  cleanUpAccessToken,
  loginWithFacebook,
  loginWithScopely,
  logoutPortalV2,
  loginWithOneTimeToken,
} from '@wwe-portal/web/services/client/auth';
import axios from 'axios';
import { createStore, StateCreator, useStore } from 'zustand';
import { toast } from '@wwe-portal/ui/components/toast';
import { tryGetDeviceToken } from '@wwe-portal/web/libs/analytics/titan';
import { jwtDecode } from 'jwt-decode';
import { getScopelyId } from '@wwe-portal/web/libs/scopelyid';
import { clientLogger } from '@wwe-portal/web/libs/client-logger';
import { addDDError } from '@wwe-portal/web/libs/analytics/datadog';
import {
  sendFacebookAuthEvent,
  sendScopelyIdAuthEvent,
} from '@wwe-portal/web/libs/analytics/auth';

const WITH_FACEBOOK = 'withFacebook';
const WITH_SCOPELY = 'withScopely';
const WITH_ONETIME_TOKEN = 'withOneTimeToken';

export const LOGIN_METHODS = {
  WITH_FACEBOOK,
  WITH_SCOPELY,
  WITH_ONETIME_TOKEN,
};

interface AuthStoreState {
  hydrated: boolean;
  authenticated: boolean;
  currentUser?: {
    userId: string;
  };
  isAuthenticating: boolean;
  loginMethod: string | null;
  loginState?: string;
}

interface AuthStoreAction {
  hydrate: () => void;
  preLoginWithScopely: (state: string) => void;
  setAuthenticating: (value: boolean) => void;
  loginWithScopely: (
    data: {
      accessToken: string;
      deviceToken: string;
      id_token: string;
    },
    isRetry?: boolean
  ) => Promise<void>;
  loginWithFacebook: (data: {
    accessToken: string;
    deviceToken: string;
  }) => void;
  loginWithOneTimeToken: (token: string) => Promise<void>;
  logout: (forget?: boolean) => Promise<void>;
  setRefreshToken: (refreshToken: string) => void;
}

export type AuthSlice = AuthStoreState & AuthStoreAction;

export const getInstantLogin = () => {
  const currentLocation = window.location.href;
  const currentURL = new URL(currentLocation);
  const params = new URLSearchParams(currentURL.search);

  return {
    isInstantLogin: params.has('oneTimeToken'),
    token: params.get('oneTimeToken'),
  };
};

const tryGetUserId = (refreshToken: string) => {
  try {
    return jwtDecode<{ userId: string }>(refreshToken).userId;
  } catch {
    return null;
  }
};

export const createAuthSlice: StateCreator<AuthSlice, [], [], AuthSlice> = (
  set,
  get
) => {
  const handleError = (error: unknown) => {
    const failedLoginMethod = get().loginMethod;
    // A failed Scopely login means the identity could not be bound to a game
    // user — server-nest's 403 case. The client guide requires clearing the
    // Scopely session on any such inconsistency, so the next attempt starts from
    // a clean IdP session instead of silently replaying the same identity.
    get().logout(failedLoginMethod === WITH_SCOPELY);
    set({
      loginMethod: null,
    });
    // The response interceptor rewraps every failure into UnexpectedError /
    // UnauthorizedError / APIError, none of which extend AxiosError, so a failed
    // login left no RUM trace at all. Report it before the toast.
    addDDError(error, {
      reason: 'login failed',
      loginMethod: failedLoginMethod,
    });
    if (!axios.isAxiosError(error)) {
      toast({
        variant: 'error',
        // Not a translation key: this store is outside React, so `useTranslations`
        // is unavailable. Kept in sync with `login_process_is_canceled` in en.json.
        content: 'Login process is canceled',
      });
    }
  };

  const setRefreshToken = (refreshToken: string) => {
    const userId = tryGetUserId(refreshToken);
    if (!userId) {
      // Login succeeded server-side but the refresh token carries no userId, so
      // the user stays unauthenticated and the login dialog never closes. Silent
      // before, and indistinguishable from the other dead ends in RUM.
      addDDError(new Error('Refresh token has no userId'), {
        reason: 'login failed',
        loginMethod: get().loginMethod,
        hasRefreshToken: !!refreshToken,
      });
      set({
        authenticated: false,
        currentUser: undefined,
      });
      return;
    }
    bridge.saveRefreshToken(refreshToken);
    set({
      authenticated: true,
      currentUser: {
        userId,
      },
    });
  };

  const clearLoginState = () => {
    set({
      authenticated: false,
      isAuthenticating: false,
      loginMethod: null,
      currentUser: undefined,
    });
  };

  return {
    hydrated: false,
    authenticated: false,
    isAuthenticating: false,
    loginMethod: null,
    hydrate: () => {
      const { isInstantLogin } = getInstantLogin();
      const refreshToken = bridge.getRefreshToken();
      const userId = tryGetUserId(refreshToken);
      const authenticated = isInstantLogin ? false : Boolean(userId);
      const currentUser =
        authenticated && userId
          ? {
              userId,
            }
          : undefined;
      set({
        hydrated: true,
        authenticated,
        currentUser,
        isAuthenticating: isInstantLogin,
        loginMethod: getLoginProvider(),
      });
    },
    setRefreshToken,
    preLoginWithScopely: (state) => {
      setLoginProvider(WITH_SCOPELY);
      set({
        loginMethod: WITH_SCOPELY,
        loginState: state,
      });
    },
    // `postLoginWithScopely` lived here: a localStorage gate the callback used to
    // decide whether it was mid-Scopely-login, which also cleared the persisted
    // login provider on success. That left `loginMethod` null after any reload,
    // so logout could not tell it needed to end the Scopely session. The callback
    // now gates on the SDK's own `isAuthorizeUri`, and the provider is cleared in
    // logout() where it belongs.
    loginWithScopely: async (
      data: {
        accessToken: string;
        deviceToken: string;
        id_token: string;
      },
      isRetry?: boolean
    ) => {
      try {
        set({ isAuthenticating: true, loginMethod: WITH_SCOPELY });
        clientLogger.debug('loginWithScopely => begin');
        const response = await loginWithScopely(data);
        clientLogger.debug('loginWithScopely => response', response);
        setRefreshToken(response.data.data.refreshToken);
        try {
          sendScopelyIdAuthEvent(
            response.data.data.userId,
            response.data.data.portalRegister
          );
        } catch (error) {
          clientLogger.debug('loginWithScopely Analytics => error', error);
        }
      } catch (e) {
        clientLogger.debug('loginWithScopely => error', e);
        if (
          axios.isAxiosError(e) &&
          e.response?.status === 409 &&
          e.response?.data.message === 'UNLINKED_ACCOUNT' &&
          !isRetry
        ) {
          return get().loginWithScopely(data, true);
        }
        handleError(e);
      } finally {
        set({ isAuthenticating: false });
      }
    },
    loginWithFacebook: async (data) => {
      try {
        set({ isAuthenticating: true, loginMethod: WITH_FACEBOOK });
        const response = await loginWithFacebook(data);
        setRefreshToken(response.data.data.refreshToken);
        try {
          sendFacebookAuthEvent(
            response.data.data.userId,
            response.data.data.portalRegister
          );
        } catch (error) {
          clientLogger.debug('loginWithFacebook Analytics => error', error);
        }
      } catch (e) {
        handleError(e);
      } finally {
        set({ isAuthenticating: false });
      }
    },
    loginWithOneTimeToken: async (token: string) => {
      try {
        set({ isAuthenticating: true, loginMethod: WITH_ONETIME_TOKEN });
        const deviceToken = await tryGetDeviceToken();
        const response = await loginWithOneTimeToken({ token, deviceToken });
        setRefreshToken(response.data.data.refreshToken);
      } catch (e) {
        handleError(e);
      } finally {
        set({ isAuthenticating: false });
        // Clear onetime token
        const url = new URL(window.location.href);
        const nextSearchParams = new URLSearchParams(url.searchParams);
        nextSearchParams.delete('oneTimeToken');
        window.history.replaceState(
          null,
          '',
          `${url.pathname}?${nextSearchParams.toString()}`
        );
      }
    },
    setAuthenticating: (value: boolean) => {
      set({ isAuthenticating: value });
    },
    logout: async (forget) => {
      // Read the login method BEFORE clearLoginState() nulls it. Reading it
      // after meant the branch below was never taken and `scopelyId.logout()`
      // never ran, so the Scopely SSO session outlived portal logout. Fall back
      // to the persisted provider for sessions restored by hydrate().
      const loginMethod = get().loginMethod ?? getLoginProvider();

      bridge.removeRefreshToken();
      try {
        await logoutPortalV2();
      } catch {
        await cleanUpAccessToken();
      }
      clearLoginState();

      if (loginMethod === WITH_SCOPELY) {
        clearLoginProvider();
        if (forget) {
          try {
            const scopelyId = await getScopelyId();
            await scopelyId.logout();
          } catch (error) {
            // Never let a dead SDK bootstrap block portal logout — the local
            // session is already gone by this point.
            addDDError(error, { reason: 'scopely logout failed' });
          }
        }
      }
    },
  };
};

export const authStore = createStore(createAuthSlice);

export const useAuthStore = () => {
  return useStore(authStore);
};

export const useIsAuthenticated = () => {
  return useStore(authStore, (state) => state.authenticated);
};

export const useIsAuthenticating = () => {
  return useStore(
    authStore,
    (state) => state.isAuthenticating || !state.hydrated
  );
};

export const useGetCurrentUserId = () => {
  return useStore(authStore, (state) => state.currentUser?.userId);
};
